import { useState } from 'react'
import { useParams, Link, useNavigate } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { api } from '@/lib/api'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
    DialogTrigger,
} from '@/components/ui/dialog'
import { toast } from '@/components/ui/toaster'
import { cn, formatRelativeTime, getProjectStatusLabel, getProjectStatusColor } from '@/lib/utils'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import {
    ArrowLeft,
    Plus,
    FileText,
    LayoutGrid,
    Users,
    Settings,
    MoreHorizontal,
    Loader2,
} from 'lucide-react'

interface Script {
    id: string
    title: string
    author: string | null
    status: string
    pageCount: number
    updatedAt: number
}

interface BeatBoard {
    id: string
    name: string
    description: string | null
    beatsCount: number
    updatedAt: number
}

interface ProjectMember {
    id: string
    userId: string
    role: string
    user: {
        id: string
        name: string
        email: string
        avatarUrl: string | null
    }
}

interface Project {
    id: string
    name: string
    description: string | null
    type: string
    status: string
    members: ProjectMember[]
    scriptsCount: number
    beatBoardsCount: number
    updatedAt: number
}

export default function ProjectPage() {
    const { projectId } = useParams<{ projectId: string }>()
    const navigate = useNavigate()
    const queryClient = useQueryClient()
    const [activeTab, setActiveTab] = useState<'scripts' | 'beat-boards' | 'team'>('scripts')
    const [isCreateScriptOpen, setIsCreateScriptOpen] = useState(false)
    const [isCreateBoardOpen, setIsCreateBoardOpen] = useState(false)
    const [newScriptTitle, setNewScriptTitle] = useState('')
    const [newBoardName, setNewBoardName] = useState('')

    const { data: projectData, isLoading: projectLoading } = useQuery({
        queryKey: ['project', projectId],
        queryFn: () => api.get<Project>(`/projects/${projectId}`),
        enabled: !!projectId,
    })

    const { data: scriptsData } = useQuery({
        queryKey: ['scripts', projectId],
        queryFn: () => api.get<Script[]>(`/projects/${projectId}/scripts`),
        enabled: !!projectId && activeTab === 'scripts',
    })

    const { data: boardsData } = useQuery({
        queryKey: ['beat-boards', projectId],
        queryFn: () => api.get<BeatBoard[]>(`/projects/${projectId}/beat-boards`),
        enabled: !!projectId && activeTab === 'beat-boards',
    })

    const createScriptMutation = useMutation({
        mutationFn: (data: { title: string }) =>
            api.post(`/projects/${projectId}/scripts`, data),
        onSuccess: (response: any) => {
            queryClient.invalidateQueries({ queryKey: ['scripts', projectId] })
            setIsCreateScriptOpen(false)
            setNewScriptTitle('')
            toast({ title: 'Script created' })
            navigate(`/projects/${projectId}/scripts/${response.data.id}`)
        },
    })

    const createBoardMutation = useMutation({
        mutationFn: (data: { name: string }) =>
            api.post(`/projects/${projectId}/beat-boards`, data),
        onSuccess: (response: any) => {
            queryClient.invalidateQueries({ queryKey: ['beat-boards', projectId] })
            setIsCreateBoardOpen(false)
            setNewBoardName('')
            toast({ title: 'Beat board created' })
            navigate(`/projects/${projectId}/beat-boards/${response.data.id}`)
        },
    })

    const project = projectData?.data
    const scripts = scriptsData?.data || []
    const beatBoards = boardsData?.data || []

    if (projectLoading) {
        return (
            <div className="flex items-center justify-center min-h-[400px]">
                <Loader2 className="h-8 w-8 animate-spin text-primary" />
            </div>
        )
    }

    if (!project) {
        return (
            <div className="flex flex-col items-center justify-center min-h-[400px]">
                <p className="text-muted-foreground mb-4">Project not found</p>
                <Button onClick={() => navigate('/')}>Back to Dashboard</Button>
            </div>
        )
    }

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex items-start justify-between">
                <div className="flex items-start gap-4">
                    <Button variant="ghost" size="icon" onClick={() => navigate('/')}>
                        <ArrowLeft className="h-5 w-5" />
                    </Button>
                    <div>
                        <div className="flex items-center gap-3 mb-1">
                            <h1 className="text-2xl font-bold">{project.name}</h1>
                            <span className={cn(
                                "px-2 py-0.5 rounded-full text-xs text-white",
                                getProjectStatusColor(project.status)
                            )}>
                                {getProjectStatusLabel(project.status)}
                            </span>
                        </div>
                        {project.description && (
                            <p className="text-muted-foreground">{project.description}</p>
                        )}
                    </div>
                </div>

                <div className="flex items-center gap-2">
                    <div className="flex -space-x-2">
                        {project.members.slice(0, 4).map((member) => (
                            <Avatar key={member.id} className="h-8 w-8 border-2 border-background">
                                <AvatarImage src={member.user.avatarUrl || undefined} />
                                <AvatarFallback className="text-xs">
                                    {member.user.name.charAt(0)}
                                </AvatarFallback>
                            </Avatar>
                        ))}
                        {project.members.length > 4 && (
                            <div className="h-8 w-8 rounded-full bg-muted flex items-center justify-center text-xs border-2 border-background">
                                +{project.members.length - 4}
                            </div>
                        )}
                    </div>
                    <Button variant="outline" size="icon">
                        <Settings className="h-4 w-4" />
                    </Button>
                </div>
            </div>

            {/* Tabs */}
            <div className="border-b">
                <nav className="flex gap-6">
                    <button
                        onClick={() => setActiveTab('scripts')}
                        className={cn(
                            "pb-3 text-sm font-medium border-b-2 transition-colors",
                            activeTab === 'scripts'
                                ? "border-primary text-foreground"
                                : "border-transparent text-muted-foreground hover:text-foreground"
                        )}
                    >
                        <FileText className="inline-block h-4 w-4 mr-2" />
                        Scripts ({project.scriptsCount})
                    </button>
                    <button
                        onClick={() => setActiveTab('beat-boards')}
                        className={cn(
                            "pb-3 text-sm font-medium border-b-2 transition-colors",
                            activeTab === 'beat-boards'
                                ? "border-primary text-foreground"
                                : "border-transparent text-muted-foreground hover:text-foreground"
                        )}
                    >
                        <LayoutGrid className="inline-block h-4 w-4 mr-2" />
                        Beat Boards ({project.beatBoardsCount})
                    </button>
                    <button
                        onClick={() => setActiveTab('team')}
                        className={cn(
                            "pb-3 text-sm font-medium border-b-2 transition-colors",
                            activeTab === 'team'
                                ? "border-primary text-foreground"
                                : "border-transparent text-muted-foreground hover:text-foreground"
                        )}
                    >
                        <Users className="inline-block h-4 w-4 mr-2" />
                        Team ({project.members.length})
                    </button>
                </nav>
            </div>

            {/* Scripts Tab */}
            {activeTab === 'scripts' && (
                <div className="space-y-4">
                    <div className="flex justify-between items-center">
                        <h2 className="text-lg font-medium">Scripts</h2>
                        <Dialog open={isCreateScriptOpen} onOpenChange={setIsCreateScriptOpen}>
                            <DialogTrigger asChild>
                                <Button size="sm">
                                    <Plus className="mr-2 h-4 w-4" />
                                    New Script
                                </Button>
                            </DialogTrigger>
                            <DialogContent>
                                <form onSubmit={(e) => { e.preventDefault(); createScriptMutation.mutate({ title: newScriptTitle }); }}>
                                    <DialogHeader>
                                        <DialogTitle>Create New Script</DialogTitle>
                                        <DialogDescription>
                                            Start a new screenplay for your project.
                                        </DialogDescription>
                                    </DialogHeader>
                                    <div className="py-4">
                                        <Label htmlFor="scriptTitle">Title</Label>
                                        <Input
                                            id="scriptTitle"
                                            placeholder="Untitled Screenplay"
                                            value={newScriptTitle}
                                            onChange={(e) => setNewScriptTitle(e.target.value)}
                                            required
                                        />
                                    </div>
                                    <DialogFooter>
                                        <Button type="button" variant="outline" onClick={() => setIsCreateScriptOpen(false)}>
                                            Cancel
                                        </Button>
                                        <Button type="submit" disabled={createScriptMutation.isPending}>
                                            {createScriptMutation.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                                            Create
                                        </Button>
                                    </DialogFooter>
                                </form>
                            </DialogContent>
                        </Dialog>
                    </div>

                    {scripts.length === 0 ? (
                        <div className="text-center py-12 border-2 border-dashed rounded-lg">
                            <FileText className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                            <h3 className="font-medium mb-2">No scripts yet</h3>
                            <p className="text-muted-foreground mb-4">Create your first screenplay</p>
                            <Button onClick={() => setIsCreateScriptOpen(true)}>
                                <Plus className="mr-2 h-4 w-4" />
                                New Script
                            </Button>
                        </div>
                    ) : (
                        <div className="grid gap-3">
                            {scripts.map((script) => (
                                <Link
                                    key={script.id}
                                    to={`/projects/${projectId}/scripts/${script.id}`}
                                    className="flex items-center justify-between p-4 border rounded-lg hover:bg-accent/50 transition-colors"
                                >
                                    <div className="flex items-center gap-3">
                                        <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                                            <FileText className="h-5 w-5 text-primary" />
                                        </div>
                                        <div>
                                            <h3 className="font-medium">{script.title}</h3>
                                            <p className="text-sm text-muted-foreground">
                                                {script.pageCount} pages • Updated {formatRelativeTime(script.updatedAt)}
                                            </p>
                                        </div>
                                    </div>
                                    <span className="text-xs px-2 py-1 bg-accent rounded-full capitalize">
                                        {script.status}
                                    </span>
                                </Link>
                            ))}
                        </div>
                    )}
                </div>
            )}

            {/* Beat Boards Tab */}
            {activeTab === 'beat-boards' && (
                <div className="space-y-4">
                    <div className="flex justify-between items-center">
                        <h2 className="text-lg font-medium">Beat Boards</h2>
                        <Dialog open={isCreateBoardOpen} onOpenChange={setIsCreateBoardOpen}>
                            <DialogTrigger asChild>
                                <Button size="sm">
                                    <Plus className="mr-2 h-4 w-4" />
                                    New Board
                                </Button>
                            </DialogTrigger>
                            <DialogContent>
                                <form onSubmit={(e) => { e.preventDefault(); createBoardMutation.mutate({ name: newBoardName }); }}>
                                    <DialogHeader>
                                        <DialogTitle>Create Beat Board</DialogTitle>
                                        <DialogDescription>
                                            Organize your story structure visually.
                                        </DialogDescription>
                                    </DialogHeader>
                                    <div className="py-4">
                                        <Label htmlFor="boardName">Name</Label>
                                        <Input
                                            id="boardName"
                                            placeholder="Story Structure"
                                            value={newBoardName}
                                            onChange={(e) => setNewBoardName(e.target.value)}
                                            required
                                        />
                                    </div>
                                    <DialogFooter>
                                        <Button type="button" variant="outline" onClick={() => setIsCreateBoardOpen(false)}>
                                            Cancel
                                        </Button>
                                        <Button type="submit" disabled={createBoardMutation.isPending}>
                                            {createBoardMutation.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                                            Create
                                        </Button>
                                    </DialogFooter>
                                </form>
                            </DialogContent>
                        </Dialog>
                    </div>

                    {beatBoards.length === 0 ? (
                        <div className="text-center py-12 border-2 border-dashed rounded-lg">
                            <LayoutGrid className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                            <h3 className="font-medium mb-2">No beat boards yet</h3>
                            <p className="text-muted-foreground mb-4">Create a board to organize your story</p>
                            <Button onClick={() => setIsCreateBoardOpen(true)}>
                                <Plus className="mr-2 h-4 w-4" />
                                New Board
                            </Button>
                        </div>
                    ) : (
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                            {beatBoards.map((board) => (
                                <Link
                                    key={board.id}
                                    to={`/projects/${projectId}/beat-boards/${board.id}`}
                                    className="block p-4 border rounded-lg hover:shadow-md transition-shadow"
                                >
                                    <div className="h-24 bg-gradient-to-br from-primary/20 to-primary/5 rounded-md mb-3 flex items-center justify-center">
                                        <LayoutGrid className="h-8 w-8 text-primary/40" />
                                    </div>
                                    <h3 className="font-medium mb-1">{board.name}</h3>
                                    <p className="text-sm text-muted-foreground">
                                        {board.beatsCount || 0} beats • Updated {formatRelativeTime(board.updatedAt)}
                                    </p>
                                </Link>
                            ))}
                        </div>
                    )}
                </div>
            )}

            {/* Team Tab */}
            {activeTab === 'team' && (
                <div className="space-y-4">
                    <div className="flex justify-between items-center">
                        <h2 className="text-lg font-medium">Team Members</h2>
                        <Button size="sm">
                            <Plus className="mr-2 h-4 w-4" />
                            Invite
                        </Button>
                    </div>

                    <div className="border rounded-lg divide-y">
                        {project.members.map((member) => (
                            <div key={member.id} className="flex items-center justify-between p-4">
                                <div className="flex items-center gap-3">
                                    <Avatar>
                                        <AvatarImage src={member.user.avatarUrl || undefined} />
                                        <AvatarFallback>{member.user.name.charAt(0)}</AvatarFallback>
                                    </Avatar>
                                    <div>
                                        <p className="font-medium">{member.user.name}</p>
                                        <p className="text-sm text-muted-foreground">{member.user.email}</p>
                                    </div>
                                </div>
                                <div className="flex items-center gap-2">
                                    <span className="text-sm capitalize px-2 py-1 bg-accent rounded-full">
                                        {member.role}
                                    </span>
                                    <Button variant="ghost" size="icon">
                                        <MoreHorizontal className="h-4 w-4" />
                                    </Button>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </div>
    )
}
